import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { fmtDate as fmtServerDate } from '../../lib/datetime';

// Anonymous free-preview limit (display fallback). The REAL enforcement is
// server-side by client IP per day (see the Node /api/analyze metering), so it
// can't be reset by clearing cookies/localStorage. This constant is only used to
// show a sensible "N left" before the server has reported the authoritative count.
const ANON_FREE_LIMIT = 3;

// Resolve the Node API base URL (same pattern as the rest of the app).
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
  return `${protocol}//${hostname}`;
};

const fmt = (n, d = 2) =>
  n === null || n === undefined || isNaN(n) ? '—' : Number(n).toFixed(d);

// Format a market cap given in BILLIONS: >= 1000B shown as trillions (e.g. 2675 -> "$2.675T"),
// otherwise billions (e.g. 850 -> "$850B"). Returns 'N/A' when missing.
const fmtMarketCap = (capB) => {
  if (capB === null || capB === undefined || isNaN(capB)) return 'N/A';
  const v = Number(capB);
  return v >= 1000 ? `$${fmt(v / 1000, 3)}T` : `$${fmt(v, 0)}B`;
};

// Default chart start date by interval (empty string = all available history):
//   WEEKLY -> last 5 years, MONTHLY -> last 10 years, QUATER -> all, DAILY -> ~1 year.
const defaultStartForInterval = (interval) => {
  const now = new Date();
  const back = (years) => {
    const d = new Date(now);
    d.setFullYear(d.getFullYear() - years);
    return d.toISOString().split('T')[0];
  };
  switch (interval) {
    case 'WEEKLY': return back(5);
    case 'MONTHLY': return back(10);
    case 'QUATER': return '';      // all history
    case 'DAILY': return back(1);
    default: return back(5);
  }
};

const ratingColor = (rating) => {
  switch (rating) {
    case 'BUY': return 'text-green-700 bg-green-100';
    case 'SELL': return 'text-red-700 bg-red-100';
    case 'HOLD': return 'text-yellow-700 bg-yellow-100';
    default: return 'text-gray-700 bg-gray-100';
  }
};

// Color for the advisory AI verdict badge (matches the Strategy page convention):
// PROCEED=green, HOLD=yellow, REDUCE=orange, SKIP=red, UNREVIEWED=gray.
const verdictColor = (verdict) => {
  switch ((verdict || '').toUpperCase()) {
    case 'PROCEED': return 'text-green-700 bg-green-100';
    case 'HOLD': return 'text-yellow-700 bg-yellow-100';
    case 'REDUCE': return 'text-orange-700 bg-orange-100';
    case 'SKIP': return 'text-red-700 bg-red-100';
    default: return 'text-gray-700 bg-gray-100';
  }
};

const Row = ({ label, value, valueClass = '' }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-100">
    <span className="text-gray-600">{label}</span>
    <span className={`font-medium ${valueClass}`}>{value}</span>
  </div>
);

// Load Plotly from a locally-bundled module (no third-party CDN), lazy-loaded as
// its own webpack chunk so it only downloads when the Analysis page actually
// renders a chart — keeping the main app bundle small while rendering fast and
// reliably (no CDN hiccup can blank the chart). The dynamic import() is cached by
// the module system, so subsequent calls resolve instantly.
let _plotlyPromise = null;
const loadPlotly = () => {
  if (window.Plotly) return Promise.resolve(window.Plotly);
  if (!_plotlyPromise) {
    _plotlyPromise = import(/* webpackChunkName: "plotly" */ 'plotly.js-dist-min')
      .then((mod) => {
        const Plotly = mod.default || mod;
        window.Plotly = Plotly; // reuse across re-renders
        return Plotly;
      })
      .catch((e) => {
        // Allow a later retry if the chunk failed to load once.
        _plotlyPromise = null;
        throw e;
      });
  }
  return _plotlyPromise;
};

const StockAnalyze = () => {
  const { user, loading: authLoading } = useAuth();
  const [symbol, setSymbol] = useState('AAPL');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const chartRef = useRef(null);

  // AI review loads asynchronously (so the page renders instantly even when the
  // backend has to generate the review via Bedrock on a cache miss).
  const [aiLoading, setAiLoading] = useState(false);

  // Anonymous free-preview metering. The limit is enforced SERVER-SIDE by IP/day
  // (not resettable via cookies); the client only reflects the server's reported
  // remaining count and shows the signup wall when the server returns 429.
  const [showWall, setShowWall] = useState(false);
  const [anonRemainingServer, setAnonRemainingServer] = useState(null); // number | null (unknown)
  const isAnon = !user;
  const anonRemaining = anonRemainingServer != null ? anonRemainingServer : ANON_FREE_LIMIT;
  const anonLimitReached = isAnon && anonRemaining <= 0;

  // Searchable stock picker (symbol + company name, so users don't need to know the
  // exact ticker). The list is fetched once from the backend; name is "N/A" when unknown.
  const [stockOptions, setStockOptions] = useState([]); // [{symbol, name}]
  const [pickerQuery, setPickerQuery] = useState('');     // what the user typed
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  // Set of symbols in the logged-in user's watchlist (to toggle Add / In-watchlist).
  const [watchlistSymbols, setWatchlistSymbols] = useState(() => new Set());
  const [watchlistBusy, setWatchlistBusy] = useState(false);

  // Authoritative subscription tier (resolved server-side from the DB, not the JWT).
  const [tier, setTier] = useState(null); // { plan, premium, status } | null

  // Fixed hover readout (pinned to the chart corner so it never covers the candles).
  const [readout, setReadout] = useState(null); // { date, o,h,l,c, vol, ... } | null

  // Company description collapse (default collapsed; toggled via Show more/less).
  const [descExpanded, setDescExpanded] = useState(false);

  // --- Chart controls ---
  const [chart, setChart] = useState(null);          // time-series payload
  const [chartLoading, setChartLoading] = useState(false);
  const [interval, setIntervalSel] = useState('WEEKLY');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [overlays, setOverlays] = useState({
    bollinger: true,
    sma: true,
    volume: true,
    macd: true,
    stochastic: true,
  });

  // Fetch the authoritative tier once, when logged in (same source the Billing
  // page uses; reads membership_type from the DB, auto-reconciling missed webhooks).
  useEffect(() => {
    if (!user) { setTier(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${getApiUrl()}/api/payments/subscription?cache=${Date.now()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) setTier(json);
      } catch (_) {/* non-fatal: banner falls back to the client user value */}
    })();
    return () => { cancelled = true; };
  }, [user]);

  const fetchStockData = async (stockSymbol, options = {}) => {
    const { countAnon = true } = options;

    setLoading(true);
    setError(null);
    setAiLoading(false);
    try {
      // The server enforces the anonymous daily limit by IP (not resettable via
      // cookies). Pass count=true for explicit searches so the server consumes one
      // credit; the initial AAPL auto-preview passes countAnon=false (no consume).
      // CRITICAL: send the member token so the server recognizes logged-in users as
      // UNLIMITED — without it the server treats them as anonymous and can 429 them
      // (showing a signup wall to a logged-in user) once their IP hits the anon cap.
      const token = localStorage.getItem('token');
      const countParam = isAnon && countAnon ? 'true' : 'false';
      const res = await fetch(
        `${getApiUrl()}/api/analyze/${encodeURIComponent(stockSymbol)}?count=${countParam}&cache=${Date.now()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      // 429 = server says the anonymous free-preview limit is reached -> signup wall.
      // Only applies to genuinely anonymous visitors.
      if (res.status === 429) {
        setAnonRemainingServer(0); // reflect "0 left" in the banner
        setShowWall(true);
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      if (json.found === false) throw new Error(`No data found for ${stockSymbol}`);
      setData(json);
      setDescExpanded(false); // new symbol -> start with the description collapsed

      // Reflect remaining anon previews from the server (authoritative) when provided.
      const remaining = res.headers.get('X-Anon-Remaining');
      if (isAnon && remaining != null && remaining !== '') {
        setAnonRemainingServer(parseInt(remaining, 10));
      }

      // AI review is now available to BOTH logged-in members and anonymous visitors
      // (the server hard-caps anon by IP, so opening the Bedrock path is safe).
      if (json.aiPending) {
        fetchAiData(json.symbol || stockSymbol);
      }
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Asynchronously fetch the advisory AI review (may trigger a Bedrock call on the
  // backend). Available to members and anon-within-limit; the server re-checks access.
  const fetchAiData = async (stockSymbol) => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiUrl()}/api/analyze/${encodeURIComponent(stockSymbol)}/ai?cache=${Date.now()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      // Only merge if the user hasn't since analyzed a different symbol.
      setData((prev) => {
        if (!prev || prev.symbol !== stockSymbol) return prev;
        return {
          ...prev,
          aiPending: false,
          ai: json.ai || prev.ai || null,
          rating: json.rating || prev.rating,
          ratingSource: json.ratingSource || prev.ratingSource,
        };
      });
    } catch (e) {
      // Non-fatal: leave the card in its "unavailable" state.
      setData((prev) => (prev && prev.symbol === stockSymbol ? { ...prev, aiPending: false } : prev));
    } finally {
      setAiLoading(false);
    }
  };

  // Fetch the time-series chart data (OHLCV + indicators) for the current controls.
  const fetchChartData = async (stockSymbol) => {
    setChartLoading(true);
    try {
      const params = new URLSearchParams({ interval, cache: Date.now() });
      // Default range by interval when the user hasn't picked a start date:
      //   WEEKLY -> last 5y, MONTHLY -> last 10y, QUATER -> all history, DAILY -> ~1y.
      const effectiveStart = startDate || defaultStartForInterval(interval);
      if (effectiveStart) params.set('start', effectiveStart);
      if (endDate) params.set('end', endDate);
      const res = await fetch(
        `${getApiUrl()}/api/analyze/${encodeURIComponent(stockSymbol)}/chart?${params.toString()}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setChart(json);
    } catch (e) {
      setChart({ error: e.message, candles: [] });
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    // Resolve the initial symbol. Preferred: path param /stock/stock-analyze/:symbol.
    // Back-compat fallbacks: ?symbol= query string, then default AAPL.
    // The initial load is a non-consuming preview for anonymous visitors.
    const pathMatch = window.location.pathname.match(/\/stock-analyze\/([^/?#]+)/i);
    const params = new URLSearchParams(window.location.search);
    const fromPath = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
    const initial = (fromPath || params.get('symbol') || symbol || 'AAPL').toUpperCase();
    setSymbol(initial);
    fetchStockData(initial, { countAnon: false });
    fetchChartData(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch chart whenever interval / date range changes (symbol stays as last analyzed).
  useEffect(() => {
    if (data?.symbol) fetchChartData(data.symbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, startDate, endDate]);

  // Load the symbol+name list once for the searchable picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/stock-list`);
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && Array.isArray(json)) setStockOptions(json);
      } catch (_) { /* non-fatal: picker falls back to free-text symbol entry */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load the logged-in user's watchlist symbols so the button reflects "already added".
  useEffect(() => {
    if (!user) { setWatchlistSymbols(new Set()); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${getApiUrl()}/api/watchlist?cache=${Date.now()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && Array.isArray(json)) {
          setWatchlistSymbols(new Set(json.map((w) => (w.symbol || '').toUpperCase())));
        }
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // On load (and whenever auth changes), fetch the authoritative anonymous
  // free-preview status so the banner shows the true "N left" for a returning
  // visitor who already used their allowance (instead of always showing the max).
  useEffect(() => {
    // Wait until auth has resolved — otherwise a logged-in user is briefly treated
    // as anonymous (user===null during load) and could flash the anon banner/wall.
    if (authLoading) return;
    if (user) { setAnonRemainingServer(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/anon-usage?cache=${Date.now()}`);
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled) return;
        if (typeof json.remaining === 'number') setAnonRemainingServer(json.remaining);
        if (json.limitReached) setShowWall(false); // don't pop the wall on mere page load
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  // Close the picker dropdown when clicking outside it.
  useEffect(() => {
    const onDocClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Draw the multi-panel chart (price + Bollinger/SMA overlays, Volume, MACD, Stochastic)
  // whenever chart data or overlay toggles change.
  useEffect(() => {
    const candles = chart?.candles;
    if (!candles || candles.length === 0 || !chartRef.current) return;

    let cancelled = false;
    loadPlotly()
      .then((Plotly) => {
        if (cancelled || !chartRef.current) return;

        const num = (v) => (v === null || v === undefined ? null : Number(v));
        const dates = candles.map((d) => d.date);
        const open = candles.map((d) => num(d.open));
        const high = candles.map((d) => num(d.high));
        const low = candles.map((d) => num(d.low));
        const close = candles.map((d) => num(d.close));
        const hasIndicators = candles.some((d) => d.sma_20 != null || d.macd != null);

        // Decide which sub-panels are active (only when the data exists).
        const showVol = overlays.volume;
        const showMacd = overlays.macd && hasIndicators;
        const showStoch = overlays.stochastic && hasIndicators;

        // Build a vertical stack: price (tall) + optional Volume / MACD / Stochastic.
        const panels = ['price'];
        if (showVol) panels.push('vol');
        if (showMacd) panels.push('macd');
        if (showStoch) panels.push('stoch');

        // Domain allocation: price gets the lion's share, sub-panels split the rest.
        const subCount = panels.length - 1;
        const priceH = subCount === 0 ? 1 : 0.55;
        const subH = subCount === 0 ? 0 : (1 - priceH - 0.03 * subCount) / subCount;
        const domains = {};
        let top = 1.0;
        panels.forEach((p, idx) => {
          const h = idx === 0 ? priceH : subH;
          const bottom = Math.max(0, top - h);
          domains[p] = [bottom, top];
          top = bottom - 0.03;
        });

        const axisFor = { price: 'y', vol: 'y2', macd: 'y3', stoch: 'y4' };
        const traces = [];

        // --- Price candles ---
        traces.push({
          x: dates, open, high, low, close,
          type: 'candlestick', name: 'Price',
          increasing: { line: { color: '#16a34a' } },
          decreasing: { line: { color: '#dc2626' } },
          xaxis: 'x', yaxis: axisFor.price,
        });

        // --- Bollinger bands ---
        if (overlays.bollinger && hasIndicators) {
          traces.push({
            x: dates, y: candles.map((d) => num(d.bollinger_20_upper)),
            type: 'scatter', mode: 'lines', name: 'BB Upper',
            line: { color: 'rgba(59,130,246,0.6)', width: 1 }, xaxis: 'x', yaxis: axisFor.price,
          });
          traces.push({
            x: dates, y: candles.map((d) => num(d.bollinger_20_lower)),
            type: 'scatter', mode: 'lines', name: 'BB Lower',
            line: { color: 'rgba(59,130,246,0.6)', width: 1 },
            fill: 'tonexty', fillcolor: 'rgba(59,130,246,0.06)', xaxis: 'x', yaxis: axisFor.price,
          });
        }

        // --- SMA overlays ---
        if (overlays.sma && hasIndicators) {
          const smaDefs = [
            ['sma_20', '#f59e0b'], ['sma_50', '#8b5cf6'], ['sma_200', '#0ea5e9'],
          ];
          smaDefs.forEach(([key, color]) => {
            if (candles.some((d) => d[key] != null)) {
              traces.push({
                x: dates, y: candles.map((d) => num(d[key])),
                type: 'scatter', mode: 'lines', name: key.replace('_', ' ').toUpperCase(),
                line: { color, width: 1.2 }, xaxis: 'x', yaxis: axisFor.price,
              });
            }
          });
        }

        // --- Volume ---
        if (showVol) {
          traces.push({
            x: dates, y: candles.map((d) => num(d.volume)),
            type: 'bar', name: 'Volume',
            marker: { color: candles.map((d) => (num(d.close) >= num(d.open) ? 'rgba(22,163,74,0.5)' : 'rgba(220,38,38,0.5)')) },
            xaxis: 'x', yaxis: axisFor.vol,
          });
        }

        // --- MACD + signal ---
        if (showMacd) {
          traces.push({
            x: dates, y: candles.map((d) => num(d.macd)),
            type: 'scatter', mode: 'lines', name: 'MACD',
            line: { color: '#2563eb', width: 1.2 }, xaxis: 'x', yaxis: axisFor.macd,
          });
          traces.push({
            x: dates, y: candles.map((d) => num(d.macd_signal)),
            type: 'scatter', mode: 'lines', name: 'Signal',
            line: { color: '#dc2626', width: 1.2 }, xaxis: 'x', yaxis: axisFor.macd,
          });
          traces.push({
            x: dates, y: candles.map((d) => (num(d.macd) != null && num(d.macd_signal) != null ? num(d.macd) - num(d.macd_signal) : null)),
            type: 'bar', name: 'MACD Hist',
            marker: { color: candles.map((d) => ((num(d.macd) - num(d.macd_signal)) >= 0 ? 'rgba(22,163,74,0.5)' : 'rgba(220,38,38,0.5)')) },
            xaxis: 'x', yaxis: axisFor.macd,
          });
        }

        // --- Stochastic %K / %D ---
        if (showStoch) {
          traces.push({
            x: dates, y: candles.map((d) => num(d.stochastic_14_k)),
            type: 'scatter', mode: 'lines', name: '%K',
            line: { color: '#2563eb', width: 1.2 }, xaxis: 'x', yaxis: axisFor.stoch,
          });
          traces.push({
            x: dates, y: candles.map((d) => num(d.stochastic_14_d)),
            type: 'scatter', mode: 'lines', name: '%D',
            line: { color: '#f59e0b', width: 1.2 }, xaxis: 'x', yaxis: axisFor.stoch,
          });
        }

        // Suppress Plotly's floating tooltips on every trace — the pinned corner
        // panel is the single source of the readout (spike crosshair still shows).
        traces.forEach((t) => { t.hoverinfo = 'none'; });

        // Layout with stacked y-axes sharing a single x-axis.
        const layout = {
          dragmode: 'pan',
          margin: { r: 12, t: 10, b: 30, l: 55 },
          showlegend: true,
          legend: { orientation: 'h', y: 1.02, x: 0 },
          paper_bgcolor: 'white',
          plot_bgcolor: 'white',
          // Spike crosshair only; the floating tooltip is DISABLED so it can't
          // cover the candles — values are shown in a fixed corner panel (plotly_hover).
          hovermode: 'x',
          xaxis: {
            type: 'date', rangeslider: { visible: false },
            anchor: panels.length > 1 ? axisFor[panels[panels.length - 1]] : 'y',
            showspikes: true, spikemode: 'across', spikesnap: 'cursor',
            spikecolor: '#9ca3af', spikethickness: 1, spikedash: 'dot',
          },
          yaxis: { title: 'Price', domain: domains.price, autorange: true },
        };
        if (showVol) layout.yaxis2 = { title: 'Vol', domain: domains.vol, autorange: true };
        if (showMacd) layout.yaxis3 = { title: 'MACD', domain: domains.macd, autorange: true };
        if (showStoch) layout.yaxis4 = { title: 'Stoch', domain: domains.stoch, range: [0, 100] };

        const config = {
          responsive: true, displayModeBar: true, scrollZoom: true,
          modeBarButtonsToRemove: ['lasso2d', 'select2d'], displaylogo: false,
        };

        Plotly.react(chartRef.current, traces, layout, config);

        // Drive a FIXED corner readout from hover (keeps the label pinned, not
        // floating over the candles). Map the hovered x back to the candle row.
        const gd = chartRef.current;
        const idxByDate = new Map(candles.map((d, i) => [String(d.date), i]));
        gd.removeAllListeners && gd.removeAllListeners('plotly_hover');
        gd.removeAllListeners && gd.removeAllListeners('plotly_unhover');
        gd.on('plotly_hover', (ev) => {
          const pt = ev.points && ev.points[0];
          if (!pt) return;
          const key = String(pt.x);
          const i = idxByDate.has(key) ? idxByDate.get(key) : pt.pointNumber;
          const d = candles[i];
          if (!d) return;
          setReadout({
            date: d.date,
            o: num(d.open), h: num(d.high), l: num(d.low), c: num(d.close),
            vol: num(d.volume),
            k: num(d.stochastic_14_k), dd: num(d.stochastic_14_d),
            macd: num(d.macd), signal: num(d.macd_signal),
          });
        });
        gd.on('plotly_unhover', () => setReadout(null));
      })
      .catch(() => {/* chart library failed to load */});

    return () => {
      cancelled = true;
    };
    // `data` is included so the draw re-runs once the chart container actually mounts.
    // On initial load the chart OHLCV fetch can resolve BEFORE the analysis payload
    // (`data`); the chart <div ref> is gated behind `data`, so without this dep the
    // first draw bails on a null chartRef and the chart stays blank until Analyze is
    // clicked. Re-running on `data` draws it as soon as the container exists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, overlays, data]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const s = symbol.toUpperCase();
    // Anonymous visitor who has used their free previews: show the signup wall
    // and don't fetch anything further.
    if (anonLimitReached) {
      setShowWall(true);
      return;
    }
    fetchStockData(s);
    fetchChartData(s);
  };

  // Run analysis for a chosen symbol (from the picker). Mirrors handleSubmit.
  const analyzeSymbolPick = (sym) => {
    const s = (sym || '').toUpperCase();
    if (!s) return;
    setSymbol(s);
    setPickerOpen(false);
    setPickerQuery('');
    if (anonLimitReached) { setShowWall(true); return; }
    fetchStockData(s);
    fetchChartData(s);
  };

  // Filter the symbol/name list by the typed query (symbol prefix first, then name
  // contains). Cap the rendered list for performance on the ~17k-row universe.
  const filteredStocks = (() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return stockOptions.slice(0, 50);
    const starts = [];
    const contains = [];
    for (const o of stockOptions) {
      const sym = (o.symbol || '').toLowerCase();
      const nm = (o.name || '').toLowerCase();
      if (sym.startsWith(q)) starts.push(o);
      else if (sym.includes(q) || nm.includes(q)) contains.push(o);
      if (starts.length >= 50) break;
    }
    return starts.concat(contains).slice(0, 50);
  })();

  const toggleOverlay = (key) =>
    setOverlays((o) => ({ ...o, [key]: !o[key] }));

  // Add the currently-analyzed symbol to the logged-in user's watchlist.
  const addToWatchlist = async () => {
    if (!data?.symbol) return;
    const sym = data.symbol.toUpperCase();
    setWatchlistBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiUrl()}/api/watchlist?symbol=${encodeURIComponent(sym)}`, {
        method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      if (res.ok && json.status === 'ok') {
        setWatchlistSymbols((prev) => new Set(prev).add(sym));
        toast.success(`${sym} added to watchlist`);
      } else toast.error(json.error || json.message || 'Could not add');
    } catch (_) { toast.error('Could not add to watchlist'); }
    finally { setWatchlistBusy(false); }
  };

  // Remove the currently-analyzed symbol from the watchlist.
  const removeFromWatchlist = async () => {
    if (!data?.symbol) return;
    const sym = data.symbol.toUpperCase();
    setWatchlistBusy(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${getApiUrl()}/api/watchlist?symbol=${encodeURIComponent(sym)}`, {
        method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setWatchlistSymbols((prev) => { const n = new Set(prev); n.delete(sym); return n; });
      toast.success(`${sym} removed from watchlist`);
    } catch (_) { toast.error('Could not remove from watchlist'); }
    finally { setWatchlistBusy(false); }
  };

  const f = data?.fundamentals || {};
  const t = data?.technicals || {};
  const candles = chart?.candles || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Anonymous free-preview banner: shows remaining free analyses + sign-up CTA. */}
        {isAnon && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-lg px-5 py-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Free preview</h2>
              <p className="text-sm text-gray-600 mt-0.5">
                {anonRemaining > 0
                  ? <>You have <span className="font-semibold text-blue-700">{anonRemaining}</span> free {anonRemaining === 1 ? 'analysis' : 'analyses'} left. Create a free account for unlimited analyses and AI reviews.</>
                  : <>You've used your free analyses. Create a free account to continue.</>}
              </p>
            </div>
            <div className="flex gap-2">
              <a href="/login" className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-700 text-sm font-semibold rounded-md hover:bg-blue-100">Sign in</a>
              <a href="/register" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700">Create free account</a>
            </div>
          </div>
        )}

        {/* Welcome / plan banner (logged-in). Tier is authoritative (DB-resolved);
            falls back to the client user value only if the tier fetch hasn't landed. */}
        {user && (() => {
          const premium = tier
            ? !!tier.premium
            : (user.subscription || '').toUpperCase() === 'PREMIUM';
          const planLabel = premium
            ? 'Premium'
            : ((user.subscription || 'Free').charAt(0).toUpperCase() + (user.subscription || 'Free').slice(1));
          return (
            <div className="mb-6 flex flex-wrap items-center justify-between gap-2 bg-white rounded-lg shadow-sm px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Welcome back, {user.firstName || user.name || 'Member'}!
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Current Plan:{' '}
                  <span className={`font-semibold ${premium ? 'text-blue-600' : 'text-gray-700'}`}>
                    {planLabel}
                  </span>
                </p>
              </div>
              {!premium && (
                <a href="/pricing" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700">
                  Upgrade to Premium
                </a>
              )}
            </div>
          );
        })()}

        {/* Header + search */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Stock Analysis</h1>
          <form onSubmit={handleSubmit} className="flex gap-4 items-end">
            <div className="flex-1 max-w-md relative" ref={pickerRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by symbol or company name
              </label>
              <input
                type="text"
                value={pickerOpen ? pickerQuery : symbol}
                onChange={(e) => {
                  setPickerQuery(e.target.value);
                  setPickerOpen(true);
                  // Keep the raw symbol in sync so a direct "AAPL" + Analyze still works.
                  setSymbol(e.target.value.toUpperCase().trim());
                }}
                onFocus={() => { setPickerOpen(true); setPickerQuery(''); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. AAPL or Apple"
                autoComplete="off"
              />
              {pickerOpen && filteredStocks.length > 0 && (
                <ul className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg text-sm">
                  {filteredStocks.map((o) => (
                    <li key={o.symbol}>
                      <button
                        type="button"
                        onClick={() => analyzeSymbolPick(o.symbol)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2"
                      >
                        <span className="font-semibold text-gray-900 min-w-[64px]">{o.symbol}</span>
                        <span className="text-gray-500 truncate">{o.name || 'N/A'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Analyzing...' : 'Analyze'}
            </button>
          </form>
          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </div>

        {data && data.found !== false && (
          <>
            {/* Overview card — matches Dashboard pick-card design (logo, stat boxes). */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-6">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <img
                        src={`https://financialmodelingprep.com/image-stock/${data.symbol}.png`}
                        alt={`${data.symbol} logo`}
                        className="w-8 h-8 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div
                        className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded flex items-center justify-center text-white font-bold text-sm"
                        style={{ display: 'none' }}
                      >
                        {data.symbol?.charAt(0)}
                      </div>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{data.symbol}</h2>
                      <p className="text-gray-600">{(() => {
                        const n = (data.name || '').trim();
                        // "Yahoo Finance"/"Yahoo" are placeholder data-source labels, not names.
                        if (!n || /^yahoo( finance)?$/i.test(n)) return data.symbol || 'Company';
                        return n;
                      })()}</p>
                      {(() => {
                        // Hide internal placeholder exchanges (DEV/BETA/STAGING); show real ones.
                        const hidden = ['DEV', 'BETA', 'STAGING'];
                        const ex = data.exchange && !hidden.includes(String(data.exchange).toUpperCase())
                          ? data.exchange : null;
                        const meta = [ex, data.currency].filter(Boolean).join(' · ');
                        return meta ? <p className="text-xs text-gray-400 mt-1">{meta}</p> : null;
                      })()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">${fmt(f.price)}</div>
                    {/* Don't flash the calculated rating before the AI review resolves: while
                        AI is pending/loading, show a spinner; once settled, show the rating
                        (AI-derived if the AI returned a verdict, else the calculated fallback). */}
                    {(data.aiPending || aiLoading) ? (
                      <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-500">
                        <svg className="animate-spin h-3.5 w-3.5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Rating…
                      </span>
                    ) : (
                      <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-bold ${ratingColor(data.rating)}`}>
                        {data.rating || 'NEUTRAL'}
                      </span>
                    )}
                    {!isAnon && (
                      <div className="mt-2">
                        {watchlistSymbols.has((data.symbol || '').toUpperCase()) ? (
                          <button
                            onClick={removeFromWatchlist}
                            disabled={watchlistBusy}
                            className="inline-flex items-center gap-1 text-sm text-green-600 hover:text-red-600 disabled:opacity-50"
                            title="In your watchlist — click to remove"
                          >
                            <span>★</span> In watchlist
                          </button>
                        ) : (
                          <button
                            onClick={addToWatchlist}
                            disabled={watchlistBusy}
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                            title="Add to your watchlist"
                          >
                            <span>☆</span> Add to watchlist
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Stat boxes: Entry (current) / Analyst Target / Upside — Dashboard style. */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Current Price</p>
                    <p className="text-2xl font-bold text-gray-900">${fmt(f.price)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Analyst Target</p>
                    <p className="text-2xl font-bold text-blue-600">{f.targetPrice && f.targetPrice > 0 ? `$${fmt(f.targetPrice)}` : 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Upside Potential</p>
                    <p className={`text-2xl font-bold ${Number(f.upsidePct) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(f.upsidePct == null || !(f.targetPrice > 0)) ? 'N/A' : `${f.upsidePct >= 0 ? '+' : ''}${fmt(f.upsidePct)}%`}
                    </p>
                  </div>
                </div>

                {/* Fundamentals grid — mirrors the Strategy card layout. */}
                {(f.pe != null || f.eps != null || f.marketCapB != null ||
                  f.revGrowth != null || f.epsGrowth != null || f.dividendYield != null) && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mt-4 text-sm">
                    {f.pe != null && (
                      <div className="flex justify-between border-b border-gray-50 py-1">
                        <span className="text-gray-500">P/E</span><span className="font-medium text-gray-900">{fmt(f.pe, 1)}</span>
                      </div>
                    )}
                    {f.eps != null && (
                      <div className="flex justify-between border-b border-gray-50 py-1">
                        <span className="text-gray-500">EPS</span><span className="font-medium text-gray-900">${fmt(f.eps)}</span>
                      </div>
                    )}
                    {f.marketCapB != null && (
                      <div className="flex justify-between border-b border-gray-50 py-1">
                        <span className="text-gray-500">Mkt Cap</span><span className="font-medium text-gray-900">{fmtMarketCap(f.marketCapB)}</span>
                      </div>
                    )}
                    {f.revGrowth != null && (
                      <div className="flex justify-between border-b border-gray-50 py-1">
                        <span className="text-gray-500">Rev Growth</span>
                        <span className={`font-medium ${Number(f.revGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{`${Number(f.revGrowth) * 100 >= 0 ? '+' : ''}${fmt(Number(f.revGrowth) * 100)}%`}</span>
                      </div>
                    )}
                    {f.epsGrowth != null && (
                      <div className="flex justify-between border-b border-gray-50 py-1">
                        <span className="text-gray-500">EPS Growth</span>
                        <span className={`font-medium ${Number(f.epsGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{`${Number(f.epsGrowth) * 100 >= 0 ? '+' : ''}${fmt(Number(f.epsGrowth) * 100)}%`}</span>
                      </div>
                    )}
                    {f.dividendYield != null && f.dividendYield > 0 && (
                      <div className="flex justify-between border-b border-gray-50 py-1">
                        <span className="text-gray-500">Div Yield</span><span className="font-medium text-gray-900">{fmt(f.dividendYield)}%</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Company description — collapsed to 3 lines with a Show more/less toggle
                    so it gives context without burying the chart & fundamentals. */}
                {data.description && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
                    <p className={`text-gray-700 leading-relaxed text-sm ${descExpanded ? '' : 'line-clamp-2'}`}>
                      {data.description}
                    </p>
                    {data.description.length > 180 && (
                      <button
                        type="button"
                        onClick={() => setDescExpanded((v) => !v)}
                        className="mt-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        {descExpanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* AI Review — advisory analysis from AWS Bedrock (Claude). Loaded asynchronously
                so the page renders instantly; shows a spinner while generating. Available to
                members AND anonymous visitors within their server-enforced daily limit. Cached
                in ai_analysis and regenerated when stale; never investment advice. */}
            {(data.ai || data.aiPending || aiLoading) && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-6">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🤖</span>
                      <h3 className="text-xl font-semibold text-gray-900">AI Review</h3>
                    </div>
                    {data.ai ? (
                      <div className="flex items-center gap-2">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${verdictColor(data.ai.verdict)}`}>
                          {data.ai.verdict || 'UNREVIEWED'}
                        </span>
                        {data.ai.confidence != null && (
                          <span className="text-sm text-gray-500">
                            {Math.round(Number(data.ai.confidence))}% confidence
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Generating…
                      </span>
                    )}
                  </div>

                  {data.ai ? (
                    <>
                      {data.ai.rationale && (
                        <p className="text-gray-700 leading-relaxed text-sm mb-3">{data.ai.rationale}</p>
                      )}

                      {data.ai.riskNotes && (
                        <div className="bg-amber-50 border border-amber-100 rounded-md p-3 mb-3">
                          <p className="text-xs font-semibold text-amber-800 mb-1">Risk notes</p>
                          <p className="text-sm text-amber-900 leading-relaxed">{data.ai.riskNotes}</p>
                        </div>
                      )}

                      <p className="text-xs text-gray-400">
                        {data.ai.asOf ? `Reviewed ${fmtServerDate(data.ai.asOf)}` : 'Advisory AI review'}
                        {' '}· advisory only, not investment advice
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Generating an AI review of this setup… this can take a few seconds.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Price chart with interval / date-range controls + indicator overlays. */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-xl font-semibold text-gray-900">Price Chart</h3>
                {chartLoading && <span className="text-xs text-gray-400">Loading…</span>}
              </div>

              {/* Controls row: interval, start/end date */}
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Interval</label>
                  <select
                    value={interval}
                    onChange={(e) => {
                      // Switching interval re-applies that interval's default range
                      // (WEEKLY 5y / MONTHLY 10y / QUATER all). Clear any manual dates.
                      setStartDate('');
                      setEndDate('');
                      setIntervalSel(e.target.value);
                    }}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUATER">Quarterly</option>
                    <option value="DAILY">Daily (price only)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {(startDate || endDate) && (
                  <button
                    onClick={() => { setStartDate(''); setEndDate(''); }}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Clear dates
                  </button>
                )}
              </div>

              {/* Overlay toggles */}
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                {[
                  ['bollinger', 'Bollinger'],
                  ['sma', 'SMA'],
                  ['volume', 'Volume'],
                  ['macd', 'MACD'],
                  ['stochastic', 'Stochastic'],
                ].map(([key, label]) => (
                  <label key={key} className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={overlays[key]}
                      onChange={() => toggleOverlay(key)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{label}</span>
                  </label>
                ))}
              </div>

              {interval === 'DAILY' && (
                <p className="text-xs text-amber-600 mb-2">
                  Indicators (Bollinger / SMA / MACD / Stochastic) are not computed on the daily interval — showing price &amp; volume only.
                </p>
              )}

              {chart?.error ? (
                <p className="text-red-600 text-sm">{chart.error}</p>
              ) : candles.length > 0 ? (
                <div className="relative w-full">
                  {/* Pinned readout — fixed to the top-left of the chart so it never
                      covers the candles; updates as the cursor moves. */}
                  {readout && (
                    <div className="absolute top-2 left-2 z-10 pointer-events-none bg-gray-900/90 text-white text-xs rounded-md px-3 py-2 shadow-lg leading-relaxed">
                      <div className="font-semibold mb-0.5">{readout.date}</div>
                      <div>
                        O <span className="font-medium">{fmt(readout.o)}</span>{'  '}
                        H <span className="font-medium">{fmt(readout.h)}</span>{'  '}
                        L <span className="font-medium">{fmt(readout.l)}</span>{'  '}
                        C <span className={`font-medium ${readout.c >= readout.o ? 'text-green-400' : 'text-red-400'}`}>{fmt(readout.c)}</span>
                      </div>
                      {readout.vol != null && <div>Vol <span className="font-medium">{Number(readout.vol).toLocaleString()}</span></div>}
                      {(readout.k != null || readout.dd != null) && (
                        <div>Stoch %K <span className="font-medium">{fmt(readout.k, 1)}</span> / %D <span className="font-medium">{fmt(readout.dd, 1)}</span></div>
                      )}
                      {(readout.macd != null || readout.signal != null) && (
                        <div>MACD <span className="font-medium">{fmt(readout.macd)}</span> / Sig <span className="font-medium">{fmt(readout.signal)}</span></div>
                      )}
                    </div>
                  )}
                  <div ref={chartRef} className="w-full" style={{ minHeight: '640px' }} />
                </div>
              ) : (
                <p className="text-gray-500">No historical price data available for this symbol / range.</p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Technicals (real) */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Technical Indicators</h3>
                <p className="text-xs text-gray-400 mb-4">Weekly · as of {t.ondate || '—'}</p>
                {data.technicals ? (
                  <div className="space-y-1">
                    <Row label="RSI (20)" value={fmt(t.rsi_20, 1)} />
                    <Row label="RSI (10)" value={fmt(t.rsi_10, 1)} />
                    <Row label="Stochastic %K / %D" value={`${fmt(t.stochastic_14_k, 1)} / ${fmt(t.stochastic_14_d, 1)}`} />
                    <Row label="CCI" value={fmt(t.cci, 1)}
                      valueClass={Number(t.cci) <= -100 ? 'text-green-600' : Number(t.cci) >= 100 ? 'text-red-600' : ''} />
                    <Row label="MACD / Signal" value={`${fmt(t.macd, 2)} / ${fmt(t.macd_signal, 2)}`}
                      valueClass={Number(t.macd) > Number(t.macd_signal) ? 'text-green-600' : 'text-red-600'} />
                    <Row label="Williams %R" value={fmt(t.williams_r, 1)} />
                    <Row label="ADX" value={fmt(t.adx, 1)} />
                    <Row label="SMA 10 / 20" value={`$${fmt(t.sma_10)} / $${fmt(t.sma_20)}`} />
                    <Row label="SMA 50 / 200" value={`$${fmt(t.sma_50)} / $${fmt(t.sma_200)}`} />
                    <Row label="Bollinger (20) Upper / Lower" value={`$${fmt(t.bollinger_20_upper)} / $${fmt(t.bollinger_20_lower)}`} />
                    <Row label="Support 1M / 3M" value={`$${fmt(t.support1m)} / $${fmt(t.support3m)}`} />
                    <Row label="Resistance 1M / 3M" value={`$${fmt(t.resistance1m)} / $${fmt(t.resistance3m)}`} />
                  </div>
                ) : (
                  <p className="text-gray-500">No technical data available for this symbol.</p>
                )}
              </div>

              {/* Fundamentals (real) */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Fundamental Metrics</h3>
                <div className="space-y-1">
                  <Row label="P/E Ratio" value={fmt(f.pe)} />
                  <Row label="EPS" value={f.eps === null || f.eps === undefined ? 'N/A' : `$${fmt(f.eps)}`} />
                  <Row label="Market Cap" value={fmtMarketCap(f.marketCapB)} />
                  <Row label="Dividend" value={f.dividend ? `$${fmt(f.dividend)}` : 'None'} />
                  <Row label="Dividend Yield" value={f.dividendYield ? `${fmt(f.dividendYield)}%` : 'None'} />
                  <Row label="52-Week High / Low" value={`$${fmt(f.week52High)} / $${fmt(f.week52Low)}`} />
                  <Row label="Analyst Target" value={f.targetPrice && f.targetPrice > 0 ? `$${fmt(f.targetPrice)}` : 'N/A'} />
                  <Row label="Upside Potential"
                    value={(f.upsidePct == null || !(f.targetPrice > 0)) ? 'N/A' : `${f.upsidePct >= 0 ? '+' : ''}${fmt(f.upsidePct)}%`}
                    valueClass={Number(f.upsidePct) > 0 ? 'text-green-600' : 'text-red-600'} />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Disclaimer */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
          <p className="text-sm text-blue-700">
            Informational only — not investment advice.
          </p>
        </div>
      </div>

      {/* Signup wall — shown to anonymous visitors once their free previews are used. */}
      {showWall && isAnon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 text-center">
            <div className="text-4xl mb-3">🔒</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Create a free account to continue</h3>
            <p className="text-gray-600 mb-5">
              You've used your {ANON_FREE_LIMIT} free analyses. Sign up for a free account to get
              unlimited stock analysis plus AI-generated reviews — no credit card required.
            </p>
            <div className="flex flex-col gap-2">
              <a href="/register" className="inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700">
                Create free account
              </a>
              <a href="/login" className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50">
                I already have an account
              </a>
              <button
                onClick={() => setShowWall(false)}
                className="mt-1 text-sm text-gray-400 hover:text-gray-600"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockAnalyze;
